// StorageUploadIntentService.js

// Firebase
import { functions, httpsCallable } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const PREPARE_UPLOAD_INTENT_FN = 'prepareBaseUploadIntent';

function callable(name) {
  return httpsCallable(functions, name);
}

/**
 * Reserves base storage for a pending upload and returns a short-lived intent.
 *
 * @param {object} input - Upload intent input.
 * @param {string} input.baseKey - Base key for plan enforcement.
 * @param {string} input.storagePath - Exact storage object path that will be written.
 * @param {number} input.fileSizeBytes - Size of the file before upload.
 * @param {string} [input.contentType] - Upload content type.
 * @param {string} [input.uploadKind] - Logical upload kind label.
 * @param {string} [input.nodeKey] - Parent or target node key.
 * @param {string} [input.itemKey] - Child item key when applicable.
 *
 * @returns {Promise<object>} Reserved upload intent payload.
 */
export async function prepareBaseUploadIntent({
  baseKey,
  storagePath,
  fileSizeBytes,
  contentType,
  uploadKind,
  nodeKey,
  itemKey,
}) {
  const cleanedBaseKey = String(baseKey || '').trim();
  const cleanedStoragePath = String(storagePath || '').trim().replace(/^\/+/, '');
  const normalizedFileSize = Number(fileSizeBytes || 0);

  if (!cleanedBaseKey || !cleanedStoragePath || !Number.isFinite(normalizedFileSize) || normalizedFileSize < 1) {
    throw new Error('baseKey, storagePath, and fileSizeBytes are required');
  }

  const fn = callable(PREPARE_UPLOAD_INTENT_FN);
  const { data } = await fn({
    contentType: String(contentType || '').trim() || null,
    fileSizeBytes: normalizedFileSize,
    itemKey: String(itemKey || '').trim() || null,
    nodeKey: String(nodeKey || '').trim() || null,
    storagePath: cleanedStoragePath,
    uploadKind: String(uploadKind || '').trim() || null,
    ...buildTenantCallableFields(cleanedBaseKey),
  });

  return data || null;
}